import { useEffect, useRef, useState } from 'react';
import { Storage } from '../../lib/storage';
import { playerSurfaceActive } from '../../lib/api/customerContext';

// ─────────────────────────────────────────────────────────────────────
// useCenterPrefs — the member's padel-centre preferences for the mobile app:
// a favourite centre (shown in the app bar) and an optional filter («show me
// only this centre»). Persisted under `mobile_centers_v1`, Storage-first.
// Centres are venue names today (see centersFrom in memberData); when they
// become a real entity only the ids stored here change meaning.
//
// NOT AVAILABLE IN MEMBER MODE (#393). `mobile_centers_v1` is a club KV key, so
// a member reading or writing it gets a 401 — which ends their session. The
// prefs fall back to the default (favourite unset, no filter) for members.
//
// That this is stored club-side at all is the real oddity: it is a per-VIEWER
// convenience, not club data, and two members of the same club share one value.
// Moving it to per-device storage would fix both problems; it is out of scope
// here and noted on #393.
// ─────────────────────────────────────────────────────────────────────

export const CENTER_PREFS_KEY = 'mobile_centers_v1';

export interface CenterPrefs {
  favoriteId: string | null;
  /** null = all centres. */
  filterId: string | null;
}

const EMPTY: CenterPrefs = { favoriteId: null, filterId: null };

export function useCenterPrefs() {
  const [prefs, setPrefsState] = useState<CenterPrefs>(EMPTY);
  const latest = useRef<CenterPrefs>(EMPTY);

  useEffect(() => {
    if (playerSurfaceActive()) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await Storage.get(CENTER_PREFS_KEY);
        if (!cancelled && res?.value) {
          const next = { ...EMPTY, ...JSON.parse(res.value) };
          latest.current = next;
          setPrefsState(next);
        }
      } catch {
        /* nothing stored yet */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Storage-first: persist, then flip state.
  const setPrefs = async (patch: Partial<CenterPrefs>) => {
    const next = { ...latest.current, ...patch };
    // Member mode keeps the choice for this page only — writing it is a club
    // key, and a 401 there ends the session (#393). Better a preference that
    // does not persist than a member signed out for picking a favourite.
    if (playerSurfaceActive()) {
      latest.current = next;
      setPrefsState(next);
      return;
    }
    await Storage.set(CENTER_PREFS_KEY, JSON.stringify(next));
    latest.current = next;
    setPrefsState(next);
  };

  return { prefs, setPrefs };
}
